import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveSessionRows } from '@/hooks/useActiveSession';
import { useSessionEndRedirect } from '@/hooks/useSessionEndRedirect';
import { MapPage } from '@/pages/map/MapPage';
import { ChargingPage } from '@/pages/charging/ChargingPage';

interface Props { onNavigate: (path: string) => void }

/**
 * '/' — the home screen. The active session owns it (frictionless flow,
 * piece 2): while the driver has a session in any live phase (pending
 * plug-in window or active)
 * the home IS the live view — kWh, cost so far, rate, elapsed, Stop —
 * with nothing behind a sheet. Otherwise it is the map.
 *
 * '/map' (the Find Charger tab) always renders the map, so a driver
 * mid-session can still look for the next stall; only the home swaps.
 * An ended idle session remains reachable through the floating bar.
 * Cold start, the push tap, the mini bar and the Active Session chip
 * all land here.
 *
 * Reads the shared ACTIVE_SESSION_KEY cache at the ambient 15s cadence;
 * the live view beneath polls the same key at 4s while mounted.
 * `data === undefined` (first fetch in flight) shows the map rather
 * than a blank — the swap to the session is one poll away and the map
 * is the right thing to be looking at until then.
 */
export function HomeRoute({ onNavigate }: Props) {
  const { t } = useTranslation('charging');
  const q = useActiveSessionRows();
  const rows = q.data;
  const inSession = rows?.some(
    (s) => s.status === 'active' || s.status === 'pending',
  ) ?? false;

  // The page beneath cannot see its own rows empty — this component
  // swaps it out for the map the moment they do — so the "follow the
  // session to its receipt" redirect lives HERE for the home, and the
  // page is told not to run its own (followEnd=false). The page raises
  // onLeaving the moment Stop / Cancel is dispatched, so the drop that
  // follows a deliberate stop is expected and not redirected twice.
  const leavingRef = useRef(false);
  useSessionEndRedirect({
    rows,
    onNavigate,
    suppressed: () => leavingRef.current,
    startFailedText: t('toast.startEnded'),
  });

  if (inSession) {
    return (
      <ChargingPage
        sessionId="active"
        onNavigate={onNavigate}
        followEnd={false}
        onLeaving={(leaving) => { leavingRef.current = leaving; }}
      />
    );
  }
  return <MapPage onNavigate={onNavigate} />;
}
